import React, { useEffect, useState } from 'react';

import { useStatistics, Report, ReportTableData, ReportTableFilter, ReportColumn } from './useStatistics';

import { ReportFilter } from './components/ReportFilter';
import { ReportLoadingSkeleton } from './components/ReportLoadingSkeleton';
import { Pagination } from '../../common/components/Pagination';
import { Select } from '../../common/elements/Select/Select';
import { BiSortAlt2 } from 'react-icons/bi';
import { 
  StatisticsContainer, 
  StyledPageTitle, 
  StatisticsSection, 
  InfoBox, 
  SectionDivider, 
  TableCard,
  StyledTable,
  StyledTableHead,
  StyledTableBody,
  PaginationWrapper 
} from './styles';
import { dateHelper } from '../../helpers';

export const Statistics: React.FC = () => {
  const {   
    reports, 
    loading, 
    loadReportTableData
  } = useStatistics();

  const [report, setReport] = useState<Report | null>(null);
  const [tableData, setTableData] = useState<ReportTableData | null>(null);

  // Pagination, ordering and filtering state
  const [ order, setOrder ] = useState<string | null>(null);
  const [ itemsPerPage, setItemsPerPage ] = useState<number>(20);
  const [ page, setPage ] = useState<number>(1);
  const [ filters, setFilters ] = useState<ReportTableFilter>({});

  useEffect(() => {
    resetTableData();
  }, [report]);

  useEffect(() => {
    if (report) {
      loadTableData();
    }
  }, [filters, itemsPerPage, page, order]);

  const loadTableData = async () => {
    if (report) {
      const tableData = await loadReportTableData(report, filters, { limit: itemsPerPage, offset: itemsPerPage * (page - 1), ordering: order });
      setTableData(tableData);
    }
  };    

  const resetTableData = () => {
    setTableData(null);
    setPage(1);
    setOrder(null);
  };

  const handleSetPage = (offset: number) => {
    setPage(offset / itemsPerPage + 1);
  };

  const handleSetItemsPerPage = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setPage(1);
  };

  const handleOrdering = (orderBy: string) => {
    const newOrderBy = order === orderBy ? `-${orderBy}` : orderBy;
    setOrder(newOrderBy);
  };

  const formatCellValue = (value: any, column: ReportColumn): string => {
    if (value === null || value === undefined || value === '') return column.defaultValue ? String(column.defaultValue) : '';
    if (column.type === 'date') return dateHelper.formatDate(value);
    if (column.type === 'select' && column.options) {
      const option = column.options.find(opt => opt.value === value);
      return option ? option.label : String(value);
    }
    return String(value);
  }

  return (
    <StatisticsContainer>
      <StyledPageTitle>
        <h1>Statistiky</h1>
      </StyledPageTitle>

      <StatisticsSection>
        <Select
          label="Report"
          options={reports.map((r) => ({ value: r.name, label: r.name }))}
          onChange={(option) => {
            setReport(reports.find(r => r.name === option?.value) || null);
          }}
        ></Select>
      </StatisticsSection>

      <StatisticsSection>
        {!report && (
          <InfoBox>
            <p>Vyberte report pro zobrazení statistik</p>
          </InfoBox>
        )}
        {report && (
          <>
            <SectionDivider />
            <ReportFilter filters={report?.filters} onSubmit={(filters) => setFilters(filters)}/>
          </>
        )}
      </StatisticsSection>

      {report && tableData && (
        <StatisticsSection>
          <TableCard>
            <StyledTable>
              <StyledTableHead>
                <tr>
                {tableData?.columns.map((column, key) => (
                  (!column.hidden && <th key={key} onClick={() => column.sortableBy ? handleOrdering(column.sortableBy!) : undefined } style={{ cursor: column.sortableBy ? 'pointer' : 'default' }}>
                    <div>
                      {column.label}
                      {column.sortableBy && (
                        <span>
                          <BiSortAlt2 />
                        </span>
                      )}
                    </div>
                  </th>
                )))}
              </tr>
            </StyledTableHead>
            <StyledTableBody>
              {loading ? (
                <ReportLoadingSkeleton columns={report.columns.length} />
              ) : tableData.rows.length === 0 ? (
                <tr>
                  <td colSpan={report.columns.length} className="no-data">Žádná data k zobrazení</td>
                </tr>
              ) : (
                tableData.rows.map((row, rowIdx) => (
                  <tr key={rowIdx} onClick={() => report.onRowClick && report.onRowClick(row)} style={{ cursor: report.onRowClick ? 'pointer' : 'default' }}>
                    {tableData.columns.map((column, colIdx) => (
                      (!column.hidden && <td key={colIdx}>
                          <span className={column.type === 'text' ? 'line-clamp-2' : ''} title={formatCellValue(row[column.key], column)}>
                            {formatCellValue(row[column.key], column)}
                          </span>
                      </td>
                    )))}
                  </tr>
                ))
              )}
            </StyledTableBody>
          </StyledTable>
          </TableCard>
        </StatisticsSection>
      )}

      {tableData && (
        <>  
          <PaginationWrapper>
            <Pagination
              count={tableData.total}
              offset={itemsPerPage * (page - 1)}
              page={page}
              limit={itemsPerPage}
              onSubmit={handleSetPage}
              onLimitChange={handleSetItemsPerPage}
            />
          </PaginationWrapper>
        </>
      )}
    </StatisticsContainer>
  );
};
